import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import type {
  DocumentIndexingItem,
  DocumentIndexingListParams,
  DocumentIndexingStatus,
} from '../types/documentIndexing';
import { keys } from './keys';

type DocumentIndexingQueryRow = {
  document_id: string;
  space_id: string;
  status: string;
  error: string | null;
  extracted_at: string | null;
  attempts: number;
  next_attempt_at: string | null;
  content: string | null;
  documents: {
    id: string;
    name: string;
    file_url: string;
    created_at: string | null;
    document_types: {
      name: string;
    } | null;
  } | null;
};

async function fetchDocumentIndexings(
  spaceId: string,
  params: DocumentIndexingListParams
): Promise<{ data: DocumentIndexingItem[]; count: number }> {
  const from = params.page * params.pageSize;
  const to = from + params.pageSize - 1;

  let query = apiClient
    .from('document_contents')
    .select(
      `
      document_id,
      space_id,
      status,
      error,
      extracted_at,
      attempts,
      next_attempt_at,
      content,
      documents!inner (
        id,
        name,
        file_url,
        created_at,
        document_types (
          name
        )
      )
    `,
      { count: 'exact' }
    )
    .eq('space_id', spaceId);

  if (params.status && params.status !== 'all') {
    query = query.eq('status', params.status);
  }

  if (params.search && params.search.trim()) {
    query = query.ilike('documents.name', `%${params.search.trim()}%`);
  }

  query = query
    .order('extracted_at', { ascending: false, nullsFirst: false })
    .order('document_id', { ascending: false })
    .range(from, to);

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  const rows = (data ?? []) as unknown as DocumentIndexingQueryRow[];

  const items: DocumentIndexingItem[] = rows.map((row) => ({
    documentId: row.document_id,
    spaceId: row.space_id,
    name: row.documents?.name ?? 'Document sans nom',
    fileUrl: row.documents?.file_url ?? '',
    createdAt: row.documents?.created_at ?? null,
    documentTypeName: row.documents?.document_types?.name ?? null,
    status: row.status as DocumentIndexingStatus,
    error: row.error,
    extractedAt: row.extracted_at,
    attempts: row.attempts,
    nextAttemptAt: row.next_attempt_at,
    hasContent: Boolean(row.content),
  }));

  return {
    data: items,
    count: count ?? 0,
  };
}

export function useGetDocumentIndexings(
  spaceId: string | null | undefined,
  params: DocumentIndexingListParams
) {
  return useQuery({
    queryKey: keys.getDocumentIndexings(spaceId ?? '', params),
    queryFn: () => fetchDocumentIndexings(spaceId!, params),
    placeholderData: keepPreviousData,
    enabled: !!spaceId,
  });
}

